import { FormRuleOperatorEnum } from '../../../../__generated__/graphql';
import { GravityFormValue } from '../../state/hooks';

export interface Rule {
  fieldId: number;
  operator: FormRuleOperatorEnum;
  value: string;
}

const cleaned = (value: null | string | undefined) => {
  return value?.toLowerCase().trim() ?? '';
};

const safeParseFloat = (value: null | string | undefined) => {
  if (value === null || value === undefined) {
    return NaN;
  }

  const parsedValue = parseFloat(value);

  if (isNaN(parsedValue)) {
    return NaN;
  }

  return parsedValue;
};

const contains = (currentValue: GravityFormValue, targetValue: string) => {
  if (Array.isArray(currentValue)) {
    return currentValue.some(value => cleaned(value).includes(cleaned(targetValue)));
  }

  return cleaned(currentValue).includes(cleaned(targetValue));
};

const greaterThan = (currentValue: GravityFormValue, targetValue: string) => {
  if (Array.isArray(currentValue)) {
    return currentValue.some(value => safeParseFloat(value) > safeParseFloat(targetValue));
  }

  return safeParseFloat(currentValue) > safeParseFloat(targetValue);
};

const is = (currentValue: GravityFormValue, targetValue: string) => {
  if (Array.isArray(currentValue)) {
    return currentValue.length === 1 && cleaned(currentValue[0]) === cleaned(targetValue);
  }

  return cleaned(currentValue) === cleaned(targetValue);
};

const isNot = (currentValue: GravityFormValue, targetValue: string) => {
  return !is(currentValue, targetValue);
};

const lessThan = (currentValue: GravityFormValue, targetValue: string) => {
  if (Array.isArray(currentValue)) {
    return currentValue.some(value => safeParseFloat(value) < safeParseFloat(targetValue));
  }

  return safeParseFloat(currentValue) < safeParseFloat(targetValue);
};

const startsWith = (currentValue: GravityFormValue, targetValue: string) => {
  if (Array.isArray(currentValue)) {
    return currentValue.some(value => cleaned(value).startsWith(cleaned(targetValue)));
  }

  return cleaned(currentValue).startsWith(cleaned(targetValue));
};

const endsWith = (currentValue: GravityFormValue, targetValue: string) => {
  if (Array.isArray(currentValue)) {
    return currentValue.some(value => cleaned(value).endsWith(cleaned(targetValue)));
  }

  return cleaned(currentValue).endsWith(cleaned(targetValue));
};

export const compare = (
  currentValue: GravityFormValue,
  operator: FormRuleOperatorEnum,
  targetValue: string,
): boolean => {
  switch (operator) {
    case FormRuleOperatorEnum.Contains:
      return contains(currentValue, targetValue);
    case FormRuleOperatorEnum.GreaterThan:
      return greaterThan(currentValue, targetValue);
    case FormRuleOperatorEnum.Is:
      return is(currentValue, targetValue);
    case FormRuleOperatorEnum.IsNot:
      return isNot(currentValue, targetValue);
    case FormRuleOperatorEnum.LessThan:
      return lessThan(currentValue, targetValue);
    case FormRuleOperatorEnum.StartsWith:
      return startsWith(currentValue, targetValue);
    case FormRuleOperatorEnum.EndsWith:
      return endsWith(currentValue, targetValue);
    default:
      return false;
  }
};
